/**
 * The headline Share of Voice definition — the ONE number every surface that
 * says "Share of Voice" on the brand dashboard must show.
 *
 * Checkpoint decision, Jul 2026: the headline pools the **consideration and
 * decision** stages. Awareness is measured and displayed per stage, but stays
 * out of the headline — it's the leading indicator, not the commercial number
 * (awareness prompts ask for lists/landscape answers that structurally dilute
 * every brand's share).
 *
 * This file is the single source of truth for that definition. It is consumed
 * by:
 *   - the brand dashboard's SoV hero (`SovModuleCard`) and visibility
 *     drill-down (client),
 *   - the org-grid batch summary endpoint (`/property-sov/summary`), and
 *   - the competitor SOV endpoint (`/:id/competitor-sov`) headline block,
 * so the org card, the hero, and the competitor sidebar can never drift
 * apart. Where the headline is null (legacy data), the org card falls back to
 * the stored all-stage `DiscoveryMetric.shareOfVoice` column.
 *
 * Pure types and pure functions — safe for `@repo/core/client`.
 */

import {
	type BreakdownPlatform,
	type DiscoveryMetricWindowRow,
	type FunnelStage,
	type PlatformKey,
	type StageWindowDelta,
	aggregateStageWindowDelta,
	coerceDiscoveryStageBreakdown,
	coerceDiscoveryTagBreakdown,
	collapseRowsByDay,
	readDiscoveryCell,
} from "./funnel-breakdown";

/**
 * The stages behind the headline Share of Voice. Exported so the funnel
 * band's bracket and the headline can never drift apart: the bracket renders
 * exactly these stages.
 */
export const HEADLINE_STAGES: readonly FunnelStage[] = [
	"consideration",
	"decision",
];

/** The headline's pooled window, in days ("vs previous 7 days"). */
export const HEADLINE_WINDOW_DAYS = 7;

export interface HeadlineSovPoint {
	measuredAt: Date;
	/** 0–100, one decimal. */
	sov: number;
	mentions: number;
	opportunities: number;
}

/**
 * The headline number and its previous-period delta, pooled across the
 * window (ratio of sums — see `aggregateStageWindow`). This is the ONLY way
 * the headline should be computed; per-stage segments call
 * `aggregateStageWindowDelta` with a single stage over the same window, so
 * the headline reconciles against them exactly.
 */
export function headlineSovWindow(
	rows: readonly DiscoveryMetricWindowRow[],
	{
		platform = "all",
		tag = null,
		end,
		days,
	}: {
		platform?: BreakdownPlatform;
		tag?: string | null;
		end: Date;
		days: number;
	},
): StageWindowDelta {
	return aggregateStageWindowDelta(rows, {
		stages: HEADLINE_STAGES,
		platform,
		tag,
		end,
		days,
	});
}

/**
 * One day's consideration + decision Share of Voice, for the **per-day
 * series** behind the sparkline and the trend chart. Derived from
 * `DiscoveryMetric.stageBreakdown`, NOT the stored all-stage `shareOfVoice`
 * column; legacy rows without a breakdown fall back to the stored values so
 * old properties don't render zeros.
 *
 * Do NOT average these points to produce the headline — a mean of daily
 * rates weights a low-volume day as heavily as a busy one and will not
 * reconcile against the per-stage numbers shown beside it. Use
 * `headlineSovWindow`, which pools the window as a ratio of sums.
 */
export function deriveHeadlineSov(
	row: {
		measuredAt: Date;
		shareOfVoice: number;
		totalMentions: number;
		stageBreakdown?: unknown;
		tagBreakdown?: unknown;
	},
	platform: BreakdownPlatform = "all",
	tag: string | null = null,
): HeadlineSovPoint | null {
	const breakdown = tag
		? (coerceDiscoveryTagBreakdown(row.tagBreakdown)?.[tag] ?? null)
		: coerceDiscoveryStageBreakdown(row.stageBreakdown);
	const consideration = readDiscoveryCell(
		breakdown,
		"consideration",
		platform,
	);
	const decision = readDiscoveryCell(breakdown, "decision", platform);

	if (consideration || decision) {
		const mentions =
			(consideration?.mentions ?? 0) + (decision?.mentions ?? 0);
		const opportunities =
			(consideration?.opportunities ?? 0) +
			(decision?.opportunities ?? 0);
		return {
			measuredAt: row.measuredAt,
			mentions,
			opportunities,
			sov:
				opportunities > 0
					? Math.round((mentions / opportunities) * 1000) / 10
					: 0,
		};
	}

	// Tag-scoped view with no data for that tag: no data, not brand-wide
	// numbers masquerading as the tag's.
	if (tag) return null;

	return {
		measuredAt: row.measuredAt,
		sov: row.shareOfVoice,
		mentions: row.totalMentions,
		opportunities: 0,
	};
}

// ---------------------------------------------------------------------------
// Stage-scoped competitor metrics — the competitorMetrics blob, v2
// ---------------------------------------------------------------------------

/** Per-(competitor × stage) counts. The v2 cell shape: an object. The v3
 *  platform- and tag-scoped cells use the compact tuple form below instead —
 *  read either through `competitorCellCounts`. */
export interface CompetitorStageCell {
	/** Completed platform executions whose answer mentions the competitor. */
	appearances: number;
	mentions: number;
}

/**
 * v3 cell: `[appearances, mentions]`.
 *
 * A tuple rather than `{ appearances, mentions }` purely for size. The v3
 * dimensions multiply out (brand × stage × platform × tag) and the row is a
 * JSON column read on every dashboard load; the tuple is ~3× smaller than the
 * object per cell, which is the difference between a ~120 KB worst-case row
 * and a ~400 KB one. Only non-zero cells are written.
 */
export type CompetitorCell = [appearances: number, mentions: number];

/** stage → cell. Only stages the brand actually appeared in are present. */
export type CompetitorStageCells = Partial<Record<FunnelStage, CompetitorCell>>;

/** platform → stage → cell. Only tracked platforms (`PlatformKey`). */
export type CompetitorPlatformCells = Partial<
	Record<PlatformKey, CompetitorStageCells>
>;

/**
 * One brand's counts inside one tag slice: the all-platform stage roll-up and
 * the per-platform split of the same slice. Tags OVERLAP — an answer whose
 * prompt carries three tags counts once in each of the three slices — so
 * slices deliberately do not sum to the unfiltered numbers.
 */
export interface CompetitorSliceCells {
	stages?: CompetitorStageCells;
	platforms?: CompetitorPlatformCells;
}

/** stage → completed platform executions (the denominators). */
export type StageOpportunities = Partial<Record<FunnelStage, number>>;

/** platform → stage → completed platform executions. */
export type PlatformStageOpportunities = Partial<
	Record<PlatformKey, StageOpportunities>
>;

/** The denominators for one tag slice, mirroring `CompetitorSliceCells`. */
export interface SliceOpportunities {
	stages?: StageOpportunities;
	platforms?: PlatformStageOpportunities;
}

export interface StageScopedCompetitorRow {
	competitorName: string;
	/** All-stage totals — the legacy chart-facing fields, unchanged. */
	mentions: number;
	/** 0–100, one decimal, all-stage — what pre-existing readers consume. */
	shareOfVoice: number;
	/** v2: per-stage counts behind the headline (C+D) competitor numbers. */
	stages?: Partial<Record<FunnelStage, CompetitorStageCell>>;
	/** v3: the same counts split by platform. Non-zero cells only. */
	platforms?: CompetitorPlatformCells;
	/** v3: tag → the same counts sliced to prompts carrying that tag. */
	tags?: Record<string, CompetitorSliceCells>;
}

/**
 * The chart-facing shape of `DiscoveryMetric.competitorMetrics` written by
 * every metric writer since the stage-scoping change. Report-sourced rows
 * store the richer report `competitorAnalysis` blob at the top level and nest
 * this record under `stageScoped`; `coerceStageScopedCompetitorMetrics`
 * accepts either layout.
 */
export interface StageScopedCompetitorMetrics {
	competitors: StageScopedCompetitorRow[];
	/**
	 * Completed platform executions per stage — the shared SoV denominators.
	 * Absent on rows written before stage scoping (those can't join a pooled
	 * headline window).
	 */
	stageOpportunities?: Partial<Record<FunnelStage, number>>;
	/**
	 * Record version. `3` (`COMPETITOR_METRICS_VERSION`) is the ONLY signal
	 * that this row carries the platform and tag dimensions.
	 *
	 * PRESENCE RULE — the thing every reader must get right:
	 *   - `v` missing (or < 3): the dimensions were never recorded for this
	 *     day. A platform- or tag-filtered read must treat the day as
	 *     **UNKNOWN** — it contributes to neither numerator nor denominator —
	 *     and report it via `daysWithDimension` so the surface can say
	 *     "platform detail starts on <date>" instead of quietly
	 *     undercounting.
	 *   - `v >= 3`: the dimensions ARE recorded. A platform or tag key that
	 *     is absent from the maps below means **recorded, zero** (nothing ran
	 *     on that platform, no prompt carried that tag), never unknown.
	 * The stage dimension is unaffected: v2 rows carry `stages` /
	 * `stageOpportunities` and keep working exactly as before.
	 */
	v?: number;
	/** v3: denominators per platform × stage. Non-zero cells only. */
	platformOpportunities?: PlatformStageOpportunities;
	/** v3: denominators per tag (stage roll-up + per-platform split). */
	tagOpportunities?: Record<string, SliceOpportunities>;
}

/**
 * The version written by the current `computeCompetitorMetricsRecord`. Bump
 * only when the stored shape changes in a way readers must branch on.
 */
export const COMPETITOR_METRICS_VERSION = 3;

/**
 * Below this many answers in a slice, a share is noise: one answer moves it
 * by five points or more. Surfaces show the number with a caution rather than
 * hiding it. One constant so the page, the export and the MCP all draw the
 * line in the same place.
 */
export const THIN_SLICE_ANSWERS = 20;

/** True when this stored record carries the v3 platform + tag dimensions. */
export function competitorRecordHasDimensions(
	record: StageScopedCompetitorMetrics | null | undefined,
): boolean {
	if (!record) return false;
	if (typeof record.v === "number" && record.v >= COMPETITOR_METRICS_VERSION)
		return true;
	// Defensive: a record that carries the new denominators is dimensioned
	// even if the marker were lost in transit.
	return !!record.platformOpportunities || !!record.tagOpportunities;
}

/** Read either cell form (v2 object, v3 tuple) as plain counts. */
export function competitorCellCounts(
	cell: CompetitorCell | CompetitorStageCell | null | undefined,
): { appearances: number; mentions: number } {
	if (!cell) return { appearances: 0, mentions: 0 };
	if (Array.isArray(cell)) {
		return { appearances: cell[0] ?? 0, mentions: cell[1] ?? 0 };
	}
	return { appearances: cell.appearances ?? 0, mentions: cell.mentions ?? 0 };
}

/**
 * Extract the stage-scoped competitor record from a raw `competitorMetrics`
 * JSON value. Handles both layouts (nightly rows store the record at the top
 * level; report rows nest it under `stageScoped` beside the rich report
 * blob). Returns `null` for legacy rows without stage data — callers must
 * treat that as "cannot join a pooled window", never fall back to mixing
 * bases.
 */
export function coerceStageScopedCompetitorMetrics(
	value: unknown,
): StageScopedCompetitorMetrics | null {
	if (!value || typeof value !== "object") return null;
	const outer = value as {
		stageScoped?: unknown;
		competitors?: unknown;
		stageOpportunities?: unknown;
	};
	const candidate =
		outer.stageScoped && typeof outer.stageScoped === "object"
			? (outer.stageScoped as StageScopedCompetitorMetrics)
			: (outer as StageScopedCompetitorMetrics);
	if (!Array.isArray(candidate.competitors)) return null;
	if (
		!candidate.stageOpportunities ||
		typeof candidate.stageOpportunities !== "object"
	) {
		return null;
	}
	return candidate;
}

export interface CompetitorWindowTotals {
	competitorName: string;
	appearances: number;
	mentions: number;
	/** 0–100, one decimal; null when the window has no opportunities. */
	sov: number | null;
}

export interface CompetitorStageWindow {
	/** Pooled platform-execution opportunities for the selected stages. */
	opportunities: number;
	competitors: CompetitorWindowTotals[];
}

const competitorKey = (name: string) => name.trim().toLowerCase();

// ---------------------------------------------------------------------------
// Sliced competitor pooling — stage × platform × tag over a window
// ---------------------------------------------------------------------------

/** Which slice of the stored record to read. */
export interface CompetitorSliceSelector {
	/** Stages to pool. All three for an unscoped "share of answers". */
	stages: readonly FunnelStage[];
	/** A single tracked platform, or null for every platform pooled. */
	platform?: PlatformKey | null;
	/** A prompt tag, or null for every prompt. Tag slices OVERLAP. */
	tag?: string | null;
}

/** True when the selector needs dimensions only v3 rows carry. */
export function sliceNeedsDimensions(
	selector: CompetitorSliceSelector,
): boolean {
	return !!selector.platform || !!selector.tag;
}

/** Pooled opportunities (answers analysed) for one row in one slice. */
export function readSliceOpportunities(
	record: StageScopedCompetitorMetrics,
	{ stages, platform = null, tag = null }: CompetitorSliceSelector,
): number {
	let total = 0;
	for (const stage of stages) {
		if (tag) {
			const slice = record.tagOpportunities?.[tag];
			total += platform
				? (slice?.platforms?.[platform]?.[stage] ?? 0)
				: (slice?.stages?.[stage] ?? 0);
			continue;
		}
		total += platform
			? (record.platformOpportunities?.[platform]?.[stage] ?? 0)
			: (record.stageOpportunities?.[stage] ?? 0);
	}
	return total;
}

/** Pooled appearances + mentions for one brand in one row's slice. */
export function readSliceCompetitorCounts(
	competitor: StageScopedCompetitorRow,
	{ stages, platform = null, tag = null }: CompetitorSliceSelector,
): { appearances: number; mentions: number } {
	let appearances = 0;
	let mentions = 0;
	for (const stage of stages) {
		const cell = tag
			? platform
				? competitor.tags?.[tag]?.platforms?.[platform]?.[stage]
				: competitor.tags?.[tag]?.stages?.[stage]
			: platform
				? competitor.platforms?.[platform]?.[stage]
				: competitor.stages?.[stage];
		const counts = competitorCellCounts(cell);
		appearances += counts.appearances;
		mentions += counts.mentions;
	}
	return { appearances, mentions };
}

export interface CompetitorSliceWindow extends CompetitorStageWindow {
	/**
	 * Measured days (post-collapse rows) inside the window — zero means the
	 * property was never measured here, which is not the same as measured and
	 * empty.
	 */
	daysMeasured: number;
	/** Days whose row carries a stage-scoped competitor record at all. */
	daysWithRecord: number;
	/**
	 * Days whose row carries the v3 platform/tag dimensions. When a filter
	 * needs them, only these days contributed; the rest are UNKNOWN, not zero.
	 */
	daysWithDimension: number;
	/** Earliest dimensioned day in the window, `YYYY-MM-DD`; null if none. */
	dimensionStartDate: string | null;
	/** True when the selector required v3 data. */
	requiresDimension: boolean;
}

const dayStamp = (d: Date | string) => new Date(d).toISOString().slice(0, 10);

type CompetitorMetricRow = DiscoveryMetricWindowRow & {
	competitorMetrics?: unknown;
};

function rowsInWindow<T extends DiscoveryMetricWindowRow>(
	rows: readonly T[],
	end: Date,
	days: number,
): T[] {
	const endMs = end.getTime();
	const startMs = endMs - days * 24 * 60 * 60 * 1000;
	return rows.filter((row) => {
		const ts = new Date(row.measuredAt).getTime();
		return ts > startMs && ts <= endMs;
	});
}

/**
 * Pool competitor share of answers across a window for ONE slice
 * (stages × platform × tag), as a ratio of sums. The generalisation of
 * `aggregateCompetitorStageWindow`: same day collapse, same denominator
 * discipline (every brand divided by the same answers-analysed count), plus
 * the v3 dimensions and the day bookkeeping a filtered surface needs.
 *
 * Returns `null` when NO day in the window carries a stage-scoped record —
 * the caller must then fall back to a legacy display in one piece rather than
 * mixing bases. A window that HAS records but no dimensioned day for a
 * filtered selector comes back with `opportunities: 0` and
 * `daysWithDimension: 0`: that is "we cannot answer this filter for these
 * days yet", and the surface says so instead of printing a zero share.
 */
export function aggregateCompetitorSliceWindow(
	rows: readonly CompetitorMetricRow[],
	options: CompetitorSliceSelector & { end: Date; days: number },
): CompetitorSliceWindow | null {
	const { end, days, stages, platform = null, tag = null } = options;
	const selector: CompetitorSliceSelector = { stages, platform, tag };
	const requiresDimension = sliceNeedsDimensions(selector);

	const windowRows = rowsInWindow(rows, end, days);
	if (!windowRows.length) return null;

	const daily = collapseRowsByDay(windowRows);
	let daysWithRecord = 0;
	let daysWithDimension = 0;
	let dimensionStartDate: string | null = null;
	let opportunities = 0;
	const byCompetitor = new Map<string, CompetitorWindowTotals>();

	for (const row of daily) {
		const record = coerceStageScopedCompetitorMetrics(
			row.competitorMetrics,
		);
		if (!record) continue;
		daysWithRecord++;

		const dimensioned = competitorRecordHasDimensions(record);
		if (dimensioned) {
			daysWithDimension++;
			const stamp = dayStamp(row.measuredAt);
			if (!dimensionStartDate || stamp < dimensionStartDate) {
				dimensionStartDate = stamp;
			}
		}
		// A day that never recorded the dimension cannot answer a filtered
		// question — counting it as zero would understate every brand.
		if (requiresDimension && !dimensioned) continue;

		opportunities += readSliceOpportunities(record, selector);

		for (const competitor of record.competitors) {
			if (
				typeof competitor?.competitorName !== "string" ||
				!competitor.competitorName.trim()
			) {
				continue;
			}
			const key = competitorKey(competitor.competitorName);
			const totals = byCompetitor.get(key) ?? {
				competitorName: competitor.competitorName.trim(),
				appearances: 0,
				mentions: 0,
				sov: null,
			};
			const counts = readSliceCompetitorCounts(competitor, selector);
			totals.appearances += counts.appearances;
			totals.mentions += counts.mentions;
			byCompetitor.set(key, totals);
		}
	}

	if (!daysWithRecord) return null;

	const competitors = [...byCompetitor.values()]
		.map((totals) => ({
			...totals,
			sov:
				opportunities > 0
					? Math.round((totals.appearances / opportunities) * 1000) /
						10
					: null,
		}))
		.sort(
			(a, b) =>
				b.appearances - a.appearances ||
				a.competitorName.localeCompare(b.competitorName),
		);

	return {
		opportunities,
		competitors,
		daysMeasured: daily.length,
		daysWithRecord,
		daysWithDimension,
		dimensionStartDate,
		requiresDimension,
	};
}

/** One day of the per-brand series behind the Brands chart. */
export interface CompetitorSeriesPoint {
	/** UTC day, `YYYY-MM-DD`. */
	date: string;
	/** Answers analysed that day in the slice. */
	opportunities: number;
	/**
	 * False when the day's row predates the requested dimension — the point is
	 * unknown, not zero, and the chart must break the line rather than plot 0.
	 */
	hasDimension: boolean;
	competitors: Array<{
		competitorName: string;
		appearances: number;
		mentions: number;
		/** 0–100, one decimal; null when the day has no answers in the slice. */
		shareOfAnswers: number | null;
	}>;
}

/**
 * The per-day series for a fixed set of brands over the same slice the table
 * pooled. Takes the brand names from the caller (the pooled ranking) so the
 * chart's lines and the table's rows can never disagree about which brands
 * are shown or what slice they describe.
 *
 * Pure — the snapshot returns the result, and `@repo/core/client` exports the
 * function for any client-side recompute.
 */
export function competitorSliceSeries(
	rows: readonly CompetitorMetricRow[],
	options: CompetitorSliceSelector & {
		end: Date;
		days: number;
		competitorNames: readonly string[];
	},
): CompetitorSeriesPoint[] {
	const { end, days, stages, platform = null, tag = null } = options;
	const selector: CompetitorSliceSelector = { stages, platform, tag };
	const requiresDimension = sliceNeedsDimensions(selector);
	const wanted = options.competitorNames.map((name) => ({
		display: name,
		key: competitorKey(name),
	}));

	return collapseRowsByDay(rowsInWindow(rows, end, days)).map((row) => {
		const record = coerceStageScopedCompetitorMetrics(
			row.competitorMetrics,
		);
		const dimensioned = competitorRecordHasDimensions(record);
		const usable = !!record && (!requiresDimension || dimensioned);
		const opportunities =
			usable && record ? readSliceOpportunities(record, selector) : 0;
		const byKey = new Map<string, StageScopedCompetitorRow>();
		if (usable && record) {
			for (const competitor of record.competitors) {
				if (typeof competitor?.competitorName !== "string") continue;
				byKey.set(competitorKey(competitor.competitorName), competitor);
			}
		}
		return {
			date: dayStamp(row.measuredAt),
			opportunities,
			hasDimension: !requiresDimension ? !!record : dimensioned,
			competitors: wanted.map(({ display, key }) => {
				const competitor = byKey.get(key);
				const counts = competitor
					? readSliceCompetitorCounts(competitor, selector)
					: { appearances: 0, mentions: 0 };
				return {
					competitorName: display,
					appearances: counts.appearances,
					mentions: counts.mentions,
					shareOfAnswers:
						opportunities > 0
							? Math.round(
									(counts.appearances / opportunities) * 1000,
								) / 10
							: null,
				};
			}),
		};
	});
}

/**
 * Pool competitor Share of Voice across a window, stage-scoped, as a ratio of
 * sums — the competitor-side mirror of `aggregateStageWindow`. Every
 * competitor shares the same denominator (total completed platform executions
 * in the selected stages), so the resulting list ranks on an identical basis
 * to the brand headline computed over the same rows.
 *
 * Rows without stage-scoped competitor data (legacy blobs) contribute
 * nothing; returns `null` when NO row in the window carries stage data, so
 * callers fall back to the legacy all-stage display in one piece rather than
 * mixing bases within a ranking.
 *
 * Thin wrapper over `aggregateCompetitorSliceWindow` with no platform or tag
 * filter — the unfiltered slice IS the headline basis, and keeping one
 * implementation is what stops the sidebar and the filtered page drifting.
 */
export function aggregateCompetitorStageWindow(
	rows: readonly (DiscoveryMetricWindowRow & {
		competitorMetrics?: unknown;
	})[],
	{
		stages,
		end,
		days,
	}: { stages: readonly FunnelStage[]; end: Date; days: number },
): CompetitorStageWindow | null {
	const window = aggregateCompetitorSliceWindow(rows, {
		stages,
		platform: null,
		tag: null,
		end,
		days,
	});
	if (!window) return null;
	return {
		opportunities: window.opportunities,
		competitors: window.competitors,
	};
}
